import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { seoHead, SITE_ORIGIN } from "@/lib/seo";
import { ExternalLink, RefreshCw, Loader2, Search } from "lucide-react";

export const Route = createFileRoute("/dev/seo-debug")({
  head: () => ({
    meta: [
      { title: "SEO Debug — Meta Tag Inspector" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Internal tool: inspects the meta tags served for a given route and links to social card validators.",
      },
    ],
  }),
  component: SeoDebugPage,
});

// Public, indexable routes worth spot-checking. Add or remove entries as the
// site grows; the free-text field below covers anything not listed.
const KNOWN_ROUTES = [
  "/",
  "/accommodations",
  "/fishing",
  "/fishing-report",
  "/fish-species",
  "/weather",
  "/gallery",
  "/reviews",
  "/booking",
  "/contact",
  "/about",
  "/faq",
  "/shop",
  "/links",
  "/submit-photo",
  "/privacy",
  "/terms",
];

type ParsedTag =
  | { kind: "title"; value: string }
  | { kind: "meta"; key: string; keyType: "name" | "property" | "http-equiv" | "charset"; value: string }
  | { kind: "link"; rel: string; href: string }
  | { kind: "jsonld"; parsed: unknown; raw: string };

function parseHead(html: string): ParsedTag[] {
  const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  const scope = headMatch ? headMatch[1] : html;
  const out: ParsedTag[] = [];

  const titleMatch = scope.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  if (titleMatch) out.push({ kind: "title", value: decodeEntities(titleMatch[1].trim()) });

  const metaRe = /<meta\b([^>]*)\/?\s*>/gi;
  for (const m of scope.matchAll(metaRe)) {
    const attrs = parseAttrs(m[1]);
    const value = attrs["content"] ?? "";
    if (attrs["charset"]) {
      out.push({ kind: "meta", keyType: "charset", key: "charset", value: attrs["charset"] });
    } else if (attrs["name"]) {
      out.push({ kind: "meta", keyType: "name", key: attrs["name"], value });
    } else if (attrs["property"]) {
      out.push({ kind: "meta", keyType: "property", key: attrs["property"], value });
    } else if (attrs["http-equiv"]) {
      out.push({ kind: "meta", keyType: "http-equiv", key: attrs["http-equiv"], value });
    }
  }

  const linkRe = /<link\b([^>]*)\/?\s*>/gi;
  for (const m of scope.matchAll(linkRe)) {
    const attrs = parseAttrs(m[1]);
    if (attrs["rel"] && attrs["href"]) {
      out.push({ kind: "link", rel: attrs["rel"], href: attrs["href"] });
    }
  }

  const jsonldRe = /<script\b[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  for (const m of scope.matchAll(jsonldRe)) {
    const raw = m[1].trim();
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = { _parseError: true, raw };
    }
    out.push({ kind: "jsonld", parsed, raw });
  }

  return out;
}

function parseAttrs(input: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
  for (const m of input.matchAll(re)) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? "";
    out[name] = decodeEntities(value);
  }
  return out;
}

function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x2F;/gi, "/");
}

// Fallback: compute the exact meta seoHead() would emit for a given path,
// without hitting the network. Doesn't include per-route JSON-LD or custom
// tags — those only show up in the "fetched HTML" view.
function computedFromHelper(path: string) {
  return seoHead({
    path,
    title: `[computed] ${path}`,
    description: "[computed] Preview of what seoHead() emits by default for this path.",
  });
}

function SeoDebugPage() {
  const [origin, setOrigin] = useState("");
  const [path, setPath] = useState("/");
  const [freeText, setFreeText] = useState("");
  const [tags, setTags] = useState<ParsedTag[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showComputed, setShowComputed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  const currentPath = freeText.trim() || path;
  const canonicalPath = currentPath.startsWith("/") ? currentPath : `/${currentPath}`;
  const previewUrl = `${SITE_ORIGIN}${canonicalPath}`;
  const fetchUrl = origin ? `${origin}${canonicalPath}` : previewUrl;

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    setTags(null);
    try {
      const res = await fetch(fetchUrl, { headers: { accept: "text/html" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      setTags(parseHead(html));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch");
    } finally {
      setLoading(false);
    }
  }, [fetchUrl]);

  useEffect(() => {
    if (origin) void fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, canonicalPath]);

  const validators = useMemo(
    () => [
      {
        label: "Facebook Sharing Debugger",
        href: `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(previewUrl)}`,
      },
      {
        label: "LinkedIn Post Inspector",
        href: `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(previewUrl)}`,
      },
      {
        label: "X (Twitter) Card Validator",
        href: `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(previewUrl)}`,
      },
      {
        label: "Google Rich Results Test",
        href: `https://search.google.com/test/rich-results?url=${encodeURIComponent(previewUrl)}`,
      },
      {
        label: "Open Graph preview (opengraph.xyz)",
        href: `https://www.opengraph.xyz/url/${encodeURIComponent(previewUrl)}`,
      },
    ],
    [previewUrl],
  );

  const grouped = useMemo(() => groupTags(tags ?? []), [tags]);
  const computed = useMemo(() => (showComputed ? computedFromHelper(canonicalPath) : null), [showComputed, canonicalPath]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <header className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ember)]/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)]">
            <Search size={14} /> Internal
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">SEO Meta Debug</h1>
          <p className="mt-2 text-muted-foreground">
            Inspects the exact meta tags, canonical link, and JSON-LD served for a route, and jumps
            straight into the major social card validators.
          </p>
        </header>

        <section className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Known routes
            </label>
            <div className="flex flex-wrap gap-2">
              {KNOWN_ROUTES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setPath(r);
                    setFreeText("");
                  }}
                  className={
                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors " +
                    (currentPath === r
                      ? "bg-[var(--color-ember-deep)] text-white border-[var(--color-ember-deep)]"
                      : "border-border hover:bg-[var(--color-parchment)]/60")
                  }
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Or any path
              </span>
              <input
                type="text"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="/fishing-report"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ember)]"
              />
            </label>
            <button
              type="button"
              onClick={fetchTags}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 self-end rounded-md bg-[var(--color-ember-deep)] px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-[var(--color-ember-deep)]/90 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              {loading ? "Fetching…" : "Refetch"}
            </button>
          </div>

          <div className="text-xs text-muted-foreground">
            Fetching from <code className="rounded bg-muted px-1 py-0.5">{fetchUrl}</code>. Validators
            below use the canonical URL <code className="rounded bg-muted px-1 py-0.5">{previewUrl}</code>.
          </div>
        </section>

        <section className="mt-6 rounded-lg border border-border bg-card p-5 sm:p-6">
          <h2 className="text-lg font-semibold mb-3">Social card validators</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {validators.map((v) => (
              <li key={v.label}>
                <a
                  href={v.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-between gap-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-[var(--color-parchment)]/60"
                >
                  {v.label}
                  <ExternalLink size={14} className="text-muted-foreground" />
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            Note: each platform caches the last scrape. After changing a tag, force a re-fetch in the
            debugger above so shared links pick up the new preview.
          </p>
        </section>

        <section className="mt-6">
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-50 px-3 py-2 text-sm text-red-800">
              Fetch failed: {error}
            </div>
          )}

          {tags && (
            <div className="space-y-5">
              <TagGroup title="Title & Description" items={grouped.core} />
              <TagGroup title="Canonical & Links" items={grouped.links} />
              <TagGroup title="Open Graph" items={grouped.og} />
              <TagGroup title="Twitter / X" items={grouped.twitter} />
              <TagGroup title="Robots & Misc Meta" items={grouped.misc} />
              <JsonLdGroup items={grouped.jsonld} />
            </div>
          )}
        </section>

        <section className="mt-8">
          <button
            type="button"
            onClick={() => setShowComputed((v) => !v)}
            className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            {showComputed ? "Hide" : "Show"} raw seoHead() output for this path
          </button>
          {computed && (
            <pre className="mt-3 max-h-96 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-xs">
              {JSON.stringify(computed, null, 2)}
            </pre>
          )}
        </section>
      </div>
    </div>
  );
}

type GroupedTags = {
  core: ParsedTag[];
  links: ParsedTag[];
  og: ParsedTag[];
  twitter: ParsedTag[];
  misc: ParsedTag[];
  jsonld: Extract<ParsedTag, { kind: "jsonld" }>[];
};

function groupTags(tags: ParsedTag[]): GroupedTags {
  const g: GroupedTags = { core: [], links: [], og: [], twitter: [], misc: [], jsonld: [] };
  for (const t of tags) {
    if (t.kind === "title") g.core.push(t);
    else if (t.kind === "jsonld") g.jsonld.push(t);
    else if (t.kind === "link") g.links.push(t);
    else if (t.kind === "meta") {
      if (t.key === "description") g.core.push(t);
      else if (t.key.startsWith("og:")) g.og.push(t);
      else if (t.key.startsWith("twitter:")) g.twitter.push(t);
      else g.misc.push(t);
    }
  }
  return g;
}

function TagGroup({ title, items }: { title: string; items: ParsedTag[] }) {
  if (items.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title} <span className="text-foreground/60">({items.length})</span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {items.map((t, i) => {
            const [key, value] = describeTag(t);
            return (
              <tr key={i} className="border-b border-border/60 last:border-b-0">
                <td className="w-1/3 max-w-[240px] truncate px-4 py-2 text-xs font-mono text-muted-foreground">
                  {key}
                </td>
                <td className="px-4 py-2 font-mono text-xs break-all">{value}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function describeTag(t: ParsedTag): [string, string] {
  if (t.kind === "title") return ["<title>", t.value];
  if (t.kind === "link") return [`link rel="${t.rel}"`, t.href];
  if (t.kind === "meta") {
    if (t.keyType === "charset") return ["meta charset", t.value];
    return [`${t.keyType}="${t.key}"`, t.value];
  }
  return ["", ""];
}

function JsonLdGroup({ items }: { items: Extract<ParsedTag, { kind: "jsonld" }>[] }) {
  if (items.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        JSON-LD <span className="text-foreground/60">({items.length})</span>
      </div>
      <div className="divide-y divide-border/60">
        {items.map((t, i) => {
          const type = extractSchemaType(t.parsed);
          return (
            <details key={i} className="p-4">
              <summary className="cursor-pointer text-sm font-medium">
                @type: <span className="font-mono">{type}</span>
              </summary>
              <pre className="mt-3 max-h-72 overflow-auto rounded-md bg-muted/40 p-3 text-xs">
                {JSON.stringify(t.parsed, null, 2)}
              </pre>
            </details>
          );
        })}
      </div>
    </div>
  );
}

function extractSchemaType(node: unknown): string {
  if (!node || typeof node !== "object") return "unknown";
  const rec = node as Record<string, unknown>;
  const t = rec["@type"];
  if (typeof t === "string") return t;
  if (Array.isArray(t)) return t.join(", ");
  if (Array.isArray(rec["@graph"])) {
    const types = (rec["@graph"] as unknown[])
      .map((n) => (n && typeof n === "object" ? (n as Record<string, unknown>)["@type"] : null))
      .filter(Boolean);
    return `@graph[${types.join(", ")}]`;
  }
  return "unknown";
}
